import { forbidExtraProps } from 'airbnb-prop-types';
import { addEventListener } from 'consolidated-events';
import isTouchDevice from 'is-touch-device';
import { DateTime } from 'luxon';
import React from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { Portal } from 'react-portal';
import { DateRangePickerPhrases } from '../defaultPhrases';
import DateRangePickerShape from '../shapes/DateRangePickerShape';
import disableScroll from '../utils/disableScroll';
import getDetachedContainerStyles from '../utils/getDetachedContainerStyles';
import getInputHeight from '../utils/getInputHeight';
import getResponsiveContainerStyles from '../utils/getResponsiveContainerStyles';
import isInclusivelyAfterDay from '../utils/isInclusivelyAfterDay';

import CloseButton from './CloseButton';
import DateRangePickerInputController from './DateRangePickerInputController';
import DayPickerRangeController from './DayPickerRangeController';

import {
    ANCHOR_LEFT,
    ANCHOR_RIGHT,
    DAY_SIZE,
    DEFAULT_VERTICAL_SPACING,
    END_DATE,
    FANG_HEIGHT_PX,
    HORIZONTAL_ORIENTATION,
    ICON_BEFORE_POSITION,
    INFO_POSITION_BOTTOM,
    NAV_POSITION_TOP,
    OPEN_DOWN,
    OPEN_UP,
    START_DATE,
    VERTICAL_ORIENTATION,
} from '../constants';
import DefaultTheme from '../theme/DefaultTheme';
import mergeClassNames from '../utils/mergeClassNames';
import mergeStyles from '../utils/mergeStyles';

const propTypes = forbidExtraProps({
    ...DateRangePickerShape,
});

const defaultProps = {
    // required props for a functional interactive DateRangePicker
    startDate: null,
    endDate: null,
    focusedInput: null,

    // input related props
    startDatePlaceholderText: 'Start Date',
    endDatePlaceholderText: 'End Date',
    startDateAriaLabel: undefined,
    endDateAriaLabel: undefined,
    startDateTitleText: undefined,
    endDateTitleText: undefined,
    startDateOffset: undefined,
    endDateOffset: undefined,
    disabled: false,
    required: false,
    readOnly: false,
    screenReaderInputMessage: '',
    showClearDates: false,
    showDefaultInputIcon: false,
    inputIconPosition: ICON_BEFORE_POSITION,
    customInputIcon: null,
    customArrowIcon: null,
    customCloseIcon: null,
    noBorder: false,
    block: false,
    small: false,
    regular: false,
    keepFocusOnInput: false,

    // calendar presentation and interaction related props
    renderMonthText: null,
    renderWeekHeaderElement: null,
    orientation: HORIZONTAL_ORIENTATION,
    anchorDirection: ANCHOR_LEFT,
    openDirection: OPEN_DOWN,
    horizontalMargin: 0,
    withPortal: false,
    withFullScreenPortal: false,
    appendToBody: false,
    alwaysOpen: false,
    disableScroll: false,
    initialVisibleMonth: null,
    numberOfMonths: 2,
    keepOpenOnDateSelect: false,
    reopenPickerOnClearDates: false,
    renderCalendarInfo: null,
    calendarInfoPosition: INFO_POSITION_BOTTOM,
    hideKeyboardShortcutsPanel: false,
    daySize: DAY_SIZE,
    isRTL: false,
    firstDayOfWeek: null,
    verticalHeight: null,
    transitionDuration: undefined,
    verticalSpacing: DEFAULT_VERTICAL_SPACING,
    autoComplete: 'off',
    horizontalMonthPadding: undefined,

    // navigation related props
    dayPickerNavigationInlineStyles: null,
    navPosition: NAV_POSITION_TOP,
    navPrev: null,
    navNext: null,
    renderNavPrevButton: null,
    renderNavNextButton: null,

    onPrevMonthClick() {},
    onNextMonthClick() {},

    onClose() {},

    // day presentation and interaction related props
    renderCalendarDay: undefined,
    renderDayContents: null,
    renderMonthElement: null,
    minimumNights: 1,
    enableOutsideDays: false,
    isDayBlocked: () => false,
    isOutsideRange: (day) => !isInclusivelyAfterDay(day, DateTime.now()),
    isDayHighlighted: () => false,
    minDate: undefined,
    maxDate: undefined,

    // internationalization
    displayFormat: () => 'MM/dd/yyyy', // Adjust format as needed
    monthFormat: 'MMMM yyyy',
    weekDayFormat: 'ccc',
    phrases: DateRangePickerPhrases,
    dayAriaLabelFormat: undefined,
};

class DateRangePicker extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            dayPickerContainerStyles: {},
            isDateRangePickerInputFocused: false,
            isDayPickerFocused: false,
            showKeyboardShortcuts: false,
        };

        this.isTouchDevice = false;

        this.onOutsideClick = this.onOutsideClick.bind(this);
        this.onDateRangePickerInputFocus = this.onDateRangePickerInputFocus.bind(this);
        this.onDayPickerFocus = this.onDayPickerFocus.bind(this);
        this.onDayPickerFocusOut = this.onDayPickerFocusOut.bind(this);
        this.onDayPickerBlur = this.onDayPickerBlur.bind(this);
        this.showKeyboardShortcutsPanel = this.showKeyboardShortcutsPanel.bind(this);

        this.responsivizePickerPosition = this.responsivizePickerPosition.bind(this);
        this.disableScroll = this.disableScroll.bind(this);

        this.setDayPickerContainerRef = this.setDayPickerContainerRef.bind(this);
        this.setContainerRef = this.setContainerRef.bind(this);
    }

    componentDidMount() {
        this.removeEventListener = addEventListener(window, 'resize', this.responsivizePickerPosition, {
            passive: true,
        });
        this.responsivizePickerPosition();
        this.disableScroll();

        const { focusedInput } = this.props;
        if (focusedInput) {
            this.setState({
                isDateRangePickerInputFocused: true,
            });
        }

        this.isTouchDevice = isTouchDevice();
    }

    componentDidUpdate(prevProps) {
        const { focusedInput } = this.props;
        if (!prevProps.focusedInput && focusedInput && this.isOpened()) {
            // The date picker just changed from being closed to being open.
            this.responsivizePickerPosition();
            this.disableScroll();
        } else if (prevProps.focusedInput && !focusedInput && !this.isOpened()) {
            // The date picker just changed from being open to being closed.
            if (this.enableScroll) this.enableScroll();
        }
    }

    componentWillUnmount() {
        this.removeDayPickerEventListeners();
        if (this.removeEventListener) this.removeEventListener();
        if (this.enableScroll) this.enableScroll();
    }

    onOutsideClick(event) {
        const { onFocusChange, onClose, startDate, endDate, appendToBody } = this.props;

        if (!this.isOpened()) return;
        if (appendToBody && this.dayPickerContainer.contains(event.target)) return;

        this.setState({
            isDateRangePickerInputFocused: false,
            isDayPickerFocused: false,
            showKeyboardShortcuts: false,
        });

        onFocusChange(null);
        onClose({ startDate, endDate });
    }

    onDateRangePickerInputFocus(focusedInput) {
        const { onFocusChange, readOnly, withPortal, withFullScreenPortal, keepFocusOnInput } = this.props;

        if (focusedInput) {
            const withAnyPortal = withPortal || withFullScreenPortal;
            const moveFocusToDayPicker =
                withAnyPortal || (readOnly && !keepFocusOnInput) || (this.isTouchDevice && !keepFocusOnInput);

            if (moveFocusToDayPicker) {
                this.onDayPickerFocus();
            } else {
                this.onDayPickerBlur();
            }
        }

        onFocusChange(focusedInput);
    }

    onDayPickerFocus() {
        const { focusedInput, onFocusChange } = this.props;
        if (!focusedInput) onFocusChange(START_DATE);

        this.setState({
            isDateRangePickerInputFocused: false,
            isDayPickerFocused: true,
            showKeyboardShortcuts: false,
        });
    }

    onDayPickerFocusOut(event) {
        // In cases where **relatedTarget** is not null, it points to the right
        // element here. However, in cases where it is null (such as clicking on a
        // specific day) or it is **document.body** (IE11), the appropriate value is **event.target**.
        //
        // We handle both situations here by using the ` || ` operator to fallback
        // to *event.target** when **relatedTarget** is not provided.
        const relatedTarget =
            event.relatedTarget === document.body ? event.target : event.relatedTarget || event.target;
        if (this.dayPickerContainer.contains(relatedTarget)) return;
        this.onOutsideClick(event);
    }

    onDayPickerBlur() {
        this.setState({
            isDateRangePickerInputFocused: true,
            isDayPickerFocused: false,
            showKeyboardShortcuts: false,
        });
    }

    setDayPickerContainerRef(ref) {
        if (ref === this.dayPickerContainer) return;
        if (this.dayPickerContainer) this.removeDayPickerEventListeners();

        this.dayPickerContainer = ref;
        if (!ref) return;

        this.addDayPickerEventListeners();
    }

    setContainerRef(ref) {
        this.container = ref;
    }

    addDayPickerEventListeners() {
        // NOTE: We are using a manual event listener here, because React doesn't
        // provide FocusOut, while blur and keydown don't provide the information
        // needed in order to know whether we have left focus or not.
        //
        // For reference, this issue is further described here:
        // - https://github.com/facebook/react/issues/6410
        this.removeDayPickerFocusOut = addEventListener(this.dayPickerContainer, 'focusout', this.onDayPickerFocusOut);
    }

    removeDayPickerEventListeners() {
        if (this.removeDayPickerFocusOut) this.removeDayPickerFocusOut();
    }

    isOpened() {
        const { focusedInput, alwaysOpen } = this.props;
        if (alwaysOpen) {
            return true;
        }
        return focusedInput === START_DATE || focusedInput === END_DATE;
    }

    disableScroll() {
        const { appendToBody, disableScroll: propDisableScroll } = this.props;
        if (!appendToBody && !propDisableScroll) return;
        if (!this.isOpened()) return;

        // Disable scroll for every ancestor of this DateRangePicker up to the
        // document level. This ensures the input and the picker never move. Other
        // sibling elements or the picker itself can scroll.
        this.enableScroll = disableScroll(this.container);
    }

    responsivizePickerPosition() {
        // It's possible the portal props have been changed in response to window resizes
        // So let's ensure we reset this back to the base state each time
        const { dayPickerContainerStyles } = this.state;

        if (Object.keys(dayPickerContainerStyles).length > 0) {
            this.setState({ dayPickerContainerStyles: {} });
        }

        if (!this.isOpened()) {
            return;
        }

        const {
            openDirection,
            anchorDirection,
            horizontalMargin,
            withPortal,
            withFullScreenPortal,
            alwaysOpen,
            appendToBody,
        } = this.props;

        const isAnchoredLeft = anchorDirection === ANCHOR_LEFT;
        if (!withPortal && !withFullScreenPortal) {
            const containerRect = this.dayPickerContainer.getBoundingClientRect();
            const currentOffset = dayPickerContainerStyles[anchorDirection] || 0;
            const containerEdge = isAnchoredLeft ? containerRect[ANCHOR_RIGHT] : containerRect[ANCHOR_LEFT];

            this.setState({
                dayPickerContainerStyles: {
                    ...getResponsiveContainerStyles(anchorDirection, currentOffset, containerEdge, horizontalMargin),
                    ...((appendToBody || alwaysOpen) &&
                        getDetachedContainerStyles(openDirection, anchorDirection, this.container)),
                },
            });
        }
    }

    showKeyboardShortcutsPanel() {
        this.setState({
            isDateRangePickerInputFocused: false,
            isDayPickerFocused: true,
            showKeyboardShortcuts: true,
        });
    }

    maybeRenderDayPickerWithPortal() {
        const { withPortal, withFullScreenPortal, appendToBody, alwaysOpen } = this.props;

        if (!this.isOpened()) {
            return null;
        }

        if (withPortal || withFullScreenPortal || appendToBody) {
            return <Portal>{this.renderDayPicker()}</Portal>;
        }
        return this.renderDayPicker();
    }

    renderDayPicker() {
        const {
            anchorDirection,
            openDirection,
            isDayBlocked,
            isDayHighlighted,
            isOutsideRange,
            numberOfMonths,
            orientation,
            monthFormat,
            renderMonthText,
            renderWeekHeaderElement,
            dayPickerNavigationInlineStyles,
            navPosition,
            navPrev,
            navNext,
            renderNavPrevButton,
            renderNavNextButton,
            onPrevMonthClick,
            onNextMonthClick,
            onDatesChange,
            onFocusChange,
            withPortal,
            withFullScreenPortal,
            daySize,
            enableOutsideDays,
            focusedInput,
            startDate,
            startDateOffset,
            endDate,
            endDateOffset,
            minDate,
            maxDate,
            minimumNights,
            keepOpenOnDateSelect,
            renderCalendarDay,
            renderDayContents,
            renderCalendarInfo,
            renderMonthElement,
            calendarInfoPosition,
            firstDayOfWeek,
            initialVisibleMonth,
            hideKeyboardShortcutsPanel,
            customCloseIcon,
            onClose,
            phrases,
            dayAriaLabelFormat,
            isRTL,
            weekDayFormat,
            verticalHeight,
            noBorder,
            transitionDuration,
            verticalSpacing,
            horizontalMonthPadding,
            small,
            disabled,
        } = this.props;

        const { dayPickerContainerStyles, isDayPickerFocused, showKeyboardShortcuts } = this.state;

        const onOutsideClick = !withFullScreenPortal && withPortal ? this.onOutsideClick : undefined;

        const initialVisibleMonthThunk = initialVisibleMonth || (() => startDate || endDate || DateTime.now());

        const closeIcon = customCloseIcon || (
            <CloseButton className={mergeClassNames('DateRangePicker_closeButton_svg')} />
        );

        const inputHeight = getInputHeight(DefaultTheme.reactDates, small);

        const withAnyPortal = withPortal || withFullScreenPortal;

        /* eslint-disable jsx-a11y/no-static-element-interactions */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        return (
            <div
                key="day-picker"
                ref={this.setDayPickerContainerRef}
                style={mergeStyles(
                    !withAnyPortal &&
                        openDirection === OPEN_DOWN && {
                            top: inputHeight + verticalSpacing,
                        },
                    !withAnyPortal &&
                        openDirection === OPEN_UP && {
                            bottom: inputHeight + verticalSpacing,
                        },
                    dayPickerContainerStyles
                )}
                className={mergeClassNames(
                    'DateRangePicker_picker',
                    anchorDirection === ANCHOR_LEFT && 'DateRangePicker_picker__directionLeft',
                    anchorDirection === ANCHOR_RIGHT && 'DateRangePicker_picker__directionRight',
                    orientation === HORIZONTAL_ORIENTATION && 'DateRangePicker_picker__horizontal',
                    orientation === VERTICAL_ORIENTATION && 'DateRangePicker_picker__vertical',
                    withAnyPortal && 'DateRangePicker_picker__portal',
                    withFullScreenPortal && 'DateRangePicker_picker__fullScreenPortal',
                    isRTL && 'DateRangePicker_picker__rtl'
                )}
                onClick={onOutsideClick}
            >
                <DayPickerRangeController
                    orientation={orientation}
                    enableOutsideDays={enableOutsideDays}
                    numberOfMonths={numberOfMonths}
                    onPrevMonthClick={onPrevMonthClick}
                    onNextMonthClick={onNextMonthClick}
                    onDatesChange={onDatesChange}
                    onFocusChange={onFocusChange}
                    onClose={onClose}
                    focusedInput={focusedInput}
                    startDate={startDate}
                    startDateOffset={startDateOffset}
                    endDate={endDate}
                    endDateOffset={endDateOffset}
                    minDate={minDate}
                    maxDate={maxDate}
                    monthFormat={monthFormat}
                    renderMonthText={renderMonthText}
                    renderWeekHeaderElement={renderWeekHeaderElement}
                    withPortal={withAnyPortal}
                    daySize={daySize}
                    initialVisibleMonth={initialVisibleMonthThunk}
                    hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
                    dayPickerNavigationInlineStyles={dayPickerNavigationInlineStyles}
                    navPosition={navPosition}
                    navPrev={navPrev}
                    navNext={navNext}
                    renderNavPrevButton={renderNavPrevButton}
                    renderNavNextButton={renderNavNextButton}
                    minimumNights={minimumNights}
                    isOutsideRange={isOutsideRange}
                    isDayHighlighted={isDayHighlighted}
                    isDayBlocked={isDayBlocked}
                    keepOpenOnDateSelect={keepOpenOnDateSelect}
                    renderCalendarDay={renderCalendarDay}
                    renderDayContents={renderDayContents}
                    renderCalendarInfo={renderCalendarInfo}
                    renderMonthElement={renderMonthElement}
                    calendarInfoPosition={calendarInfoPosition}
                    isFocused={isDayPickerFocused}
                    showKeyboardShortcuts={showKeyboardShortcuts}
                    onBlur={this.onDayPickerBlur}
                    phrases={phrases}
                    dayAriaLabelFormat={dayAriaLabelFormat}
                    isRTL={isRTL}
                    firstDayOfWeek={firstDayOfWeek}
                    weekDayFormat={weekDayFormat}
                    verticalHeight={verticalHeight}
                    noBorder={noBorder}
                    transitionDuration={transitionDuration}
                    disabled={disabled}
                    horizontalMonthPadding={horizontalMonthPadding}
                />
                {withFullScreenPortal && (
                    <button
                        className={mergeClassNames('DateRangePicker_closeButton')}
                        type="button"
                        onClick={this.onOutsideClick}
                        aria-label={phrases.closeDatePicker}
                        tabIndex="-1"
                    >
                        {closeIcon}
                    </button>
                )}
            </div>
        );
        /* eslint-enable jsx-a11y/no-static-element-interactions */
        /* eslint-enable jsx-a11y/click-events-have-key-events */
    }

    render() {
        const {
            startDate,
            startDateId,
            startDatePlaceholderText,
            startDateAriaLabel,
            startDateTitleText,
            endDate,
            endDateId,
            endDatePlaceholderText,
            endDateAriaLabel,
            endDateTitleText,
            focusedInput,
            screenReaderInputMessage,
            showClearDates,
            showDefaultInputIcon,
            inputIconPosition,
            customInputIcon,
            customArrowIcon,
            customCloseIcon,
            disabled,
            required,
            readOnly,
            autoComplete,
            openDirection,
            phrases,
            isOutsideRange,
            isDayBlocked,
            minimumNights,
            withPortal,
            withFullScreenPortal,
            displayFormat,
            reopenPickerOnClearDates,
            keepOpenOnDateSelect,
            onDatesChange,
            onClose,
            isRTL,
            noBorder,
            block,
            verticalSpacing,
            small,
            regular,
        } = this.props;

        const { isDateRangePickerInputFocused } = this.state;

        const enableOutsideClick = !withPortal && !withFullScreenPortal;

        const hideFang = verticalSpacing < FANG_HEIGHT_PX;

        const input = (
            <DateRangePickerInputController
                startDate={startDate}
                startDateId={startDateId}
                startDatePlaceholderText={startDatePlaceholderText}
                isStartDateFocused={focusedInput === START_DATE}
                startDateAriaLabel={startDateAriaLabel}
                startDateTitleText={startDateTitleText}
                endDate={endDate}
                endDateId={endDateId}
                endDatePlaceholderText={endDatePlaceholderText}
                isEndDateFocused={focusedInput === END_DATE}
                endDateAriaLabel={endDateAriaLabel}
                endDateTitleText={endDateTitleText}
                displayFormat={displayFormat}
                showClearDates={showClearDates}
                showCaret={!withPortal && !withFullScreenPortal && !hideFang}
                showDefaultInputIcon={showDefaultInputIcon}
                inputIconPosition={inputIconPosition}
                customInputIcon={customInputIcon}
                customArrowIcon={customArrowIcon}
                customCloseIcon={customCloseIcon}
                disabled={disabled}
                required={required}
                readOnly={readOnly}
                openDirection={openDirection}
                reopenPickerOnClearDates={reopenPickerOnClearDates}
                keepOpenOnDateSelect={keepOpenOnDateSelect}
                isOutsideRange={isOutsideRange}
                isDayBlocked={isDayBlocked}
                minimumNights={minimumNights}
                withFullScreenPortal={withFullScreenPortal}
                onDatesChange={onDatesChange}
                onFocusChange={this.onDateRangePickerInputFocus}
                onKeyDownArrowDown={this.onDayPickerFocus}
                onKeyDownQuestionMark={this.showKeyboardShortcutsPanel}
                onClose={onClose}
                phrases={phrases}
                screenReaderMessage={screenReaderInputMessage}
                isFocused={isDateRangePickerInputFocused}
                isRTL={isRTL}
                noBorder={noBorder}
                block={block}
                small={small}
                regular={regular}
                verticalSpacing={verticalSpacing}
                autoComplete={autoComplete}
            >
                {this.maybeRenderDayPickerWithPortal()}
            </DateRangePickerInputController>
        );

        return (
            <div
                ref={this.setContainerRef}
                className={mergeClassNames('DateRangePicker', block && 'DateRangePicker__block')}
            >
                {enableOutsideClick && (
                    <OutsideClickHandler onOutsideClick={this.onOutsideClick}>{input}</OutsideClickHandler>
                )}
                {enableOutsideClick || input}
            </div>
        );
    }
}

DateRangePicker.propTypes = propTypes;
DateRangePicker.defaultProps = defaultProps;

export { DateRangePicker as PureDateRangePicker };
export default DateRangePicker;
